import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { postsApi, mediaUrl } from '../../services/postsApi';
import { connectionsApi } from '../../services/messagesApi';
import OfficialBadge from './OfficialBadge';
import { reasonText } from './SuggestedForYou';

const MIN_PICKS = 5;

/** New members who follow no one get a one-time "pick 5+ people" starter list. */
const StarterFollowDialog = ({ language = 'en', currentUserId, onDone }) => {
  const es = language === 'es';
  const key = `lumera_starter_follow_${currentUserId}`;
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUserId || localStorage.getItem(key)) return;
    (async () => {
      try {
        const following = await connectionsApi.following();
        if ((following || []).length > 0) { localStorage.setItem(key, '1'); return; }
        const list = await postsApi.suggestions(20);
        if (!list.length) return;
        setUsers(list);
        setPicked(new Set(list.filter((u) => u.is_official).map((u) => u.user_id)));
        setOpen(true);
      } catch { /* non-blocking */ }
    })();
  }, [currentUserId, key]);

  const toggle = (uid) => setPicked((s) => { const n = new Set(s); if (n.has(uid)) n.delete(uid); else n.add(uid); return n; });
  const close = () => { localStorage.setItem(key, '1'); setOpen(false); };
  const need = Math.min(MIN_PICKS, users.length);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await postsApi.followMany([...picked]);
      toast.success(es ? `Ahora sigues a ${res.followed} personas` : `You're now following ${res.followed} people`);
      close();
      onDone?.();
    } catch {
      toast.error(es ? 'No se pudo seguir' : 'Could not follow right now');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid="starter-follow-dialog">
        <DialogTitle>{es ? 'Encuentra a tu comunidad' : 'Find your community'}</DialogTitle>
        <DialogDescription>
          {es ? `Sigue al menos ${need} personas para llenar tu feed de fe e inspiración.` : `Follow at least ${need} people to fill your feed with faith and encouragement.`}
        </DialogDescription>
        <div className="max-h-80 space-y-1 overflow-y-auto pr-1">
          {users.map((u) => {
            const on = picked.has(u.user_id);
            return (
              <button key={u.user_id} type="button" onClick={() => toggle(u.user_id)}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors ${on ? 'bg-primary/10' : 'hover:bg-muted'}`}
                data-testid={`starter-pick-${u.user_id}`}>
                <Avatar className="h-10 w-10">
                  {u.picture ? <AvatarImage src={mediaUrl(u.picture)} /> : null}
                  <AvatarFallback>{(u.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1 text-sm font-medium"><span className="truncate">{u.name}</span>{u.is_official && <OfficialBadge className="h-3.5 w-3.5" />}</span>
                  <span className="block text-xs text-muted-foreground">{reasonText(u, es)}</span>
                </span>
                <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${on ? 'border-primary bg-primary text-primary-foreground' : ''}`}>
                  {on && <Check className="h-3.5 w-3.5" />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 pt-2">
          <Button variant="ghost" onClick={close} data-testid="starter-follow-skip">{es ? 'Ahora no' : 'Not now'}</Button>
          <Button className="ml-auto rounded-full" disabled={picked.size < need || saving} onClick={submit} data-testid="starter-follow-submit">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {es ? `Seguir (${picked.size})` : `Follow (${picked.size})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StarterFollowDialog;
